/**
 * PRTL-705: personal calendar reminder types — renewals, expirations,
 * birthdays, work anniversaries and performance reviews.
 *
 * A personal event (CompassEvent) has a `kind`, may be all-day, may repeat
 * every year, and may carry a lead-time reminder ("2 weeks before"). This
 * module is the one place both calendars, the server and the reminder tasks
 * get the presets and the yearly expansion from.
 *
 * All-day entries use the imported-calendar convention (see calendarSources):
 * `start` is UTC midnight of the date, `end` (exclusive) UTC midnight of the
 * day after the last day. A birthday is Mar 4 everywhere.
 */
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { allDayDateKey } from "./calendarSources";

dayjs.extend(utc);
dayjs.extend(timezone);

export const COMPASS_EVENT_KINDS = [
    "EVENT",
    "RENEWAL",
    "EXPIRATION",
    "BIRTHDAY",
    "WORK_ANNIVERSARY",
    "PERFORMANCE_REVIEW",
] as const;

export type CompassEventKind = (typeof COMPASS_EVENT_KINDS)[number];

/** Lead times offered in the event forms, in days. */
export const LEAD_TIME_OPTIONS = [1, 7, 14, 30] as const;

export interface EventKindPreset {
    label: string;
    /** What a new entry of this kind starts with; the user can change each. */
    isAllDay: boolean;
    repeatYearly: boolean;
    leadTimeDays: number | null;
}

export const EVENT_KIND_PRESETS: Record<CompassEventKind, EventKindPreset> = {
    EVENT: { label: "Event", isAllDay: false, repeatYearly: false, leadTimeDays: null },
    RENEWAL: { label: "Renewal", isAllDay: true, repeatYearly: true, leadTimeDays: 14 },
    EXPIRATION: { label: "Expiration", isAllDay: true, repeatYearly: false, leadTimeDays: 14 },
    BIRTHDAY: { label: "Birthday", isAllDay: true, repeatYearly: true, leadTimeDays: 1 },
    WORK_ANNIVERSARY: {
        label: "Work anniversary",
        isAllDay: true,
        repeatYearly: true,
        leadTimeDays: 1,
    },
    PERFORMANCE_REVIEW: {
        label: "Performance review",
        isAllDay: true,
        repeatYearly: false,
        leadTimeDays: 7,
    },
};

export function eventKindLabel(kind: CompassEventKind): string {
    return EVENT_KIND_PRESETS[kind].label;
}

/** "1 day before", "1 week before", "2 weeks before", "30 days before". */
export function leadTimeLabel(days: number): string {
    if (days === 1) return "1 day before";
    if (days % 7 === 0 && days <= 28) {
        const weeks = days / 7;
        return weeks === 1 ? "1 week before" : `${weeks} weeks before`;
    }
    return `${days} days before`;
}

// ---- yearly expansion --------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

export interface RepeatableEvent {
    id: number;
    start: Date;
    end: Date | null;
    isAllDay: boolean;
    repeatYearly: boolean;
}

export interface EventOccurrence {
    start: Date;
    end: Date | null;
    /** Calendar year of this occurrence's date. */
    year: number;
    /** Stable per occurrence: `<event id>:<year>`. Reminders and list keys use it. */
    occurrenceKey: string;
}

export function occurrenceKey(eventId: number, year: number): string {
    return `${eventId}:${year}`;
}

function isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** Day of month in `year` for a date that was `month`/`day`: Feb 29 → Feb 28 outside leap years. */
function dayInYear(year: number, month: number, day: number): number {
    return month === 2 && day === 29 && !isLeapYear(year) ? 28 : day;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** The occurrence of `event` in `year` (no range check). */
export function occurrenceInYear(
    event: RepeatableEvent,
    year: number,
    timeZone: string,
): EventOccurrence {
    const duration = event.end ? event.end.getTime() - event.start.getTime() : null;
    let start: Date;
    if (event.isAllDay) {
        const [, m, d] = allDayDateKey(event.start).split("-").map(Number);
        start = new Date(Date.UTC(year, m - 1, dayInYear(year, m, d)));
    } else {
        // Timed repeats keep their local wall-clock time across DST.
        const local = dayjs(event.start).tz(timeZone);
        const m = local.month() + 1;
        const d = dayInYear(year, m, local.date());
        start = dayjs
            .tz(`${year}-${pad(m)}-${pad(d)} ${pad(local.hour())}:${pad(local.minute())}`, timeZone)
            .toDate();
    }
    return {
        start,
        end: duration === null ? null : new Date(start.getTime() + duration),
        year,
        occurrenceKey: occurrenceKey(event.id, year),
    };
}

/** The year an event's date falls in: UTC date for all-day, local for timed. */
export function eventYear(event: { start: Date; isAllDay: boolean }, timeZone: string): number {
    return event.isAllDay
        ? Number(allDayDateKey(event.start).slice(0, 4))
        : dayjs(event.start).tz(timeZone).year();
}

/**
 * The occurrences of `event` overlapping `[rangeStart, rangeEnd)`.
 *
 * A non-repeating event is its own single occurrence when it overlaps. A
 * yearly event repeats on the same date (all-day) or the same local time
 * (timed, in `timeZone`) every year from its first year on — never before it.
 * Feb 29 falls on Feb 28 in years without one.
 */
export function expandYearly(
    event: RepeatableEvent,
    rangeStart: Date,
    rangeEnd: Date,
    timeZone: string,
): EventOccurrence[] {
    const overlaps = (o: EventOccurrence) => {
        const end = o.end ? o.end.getTime() : o.start.getTime() + (event.isAllDay ? DAY_MS : 0);
        // A zero-length timed event overlaps when its start is inside.
        return (
            o.start.getTime() < rangeEnd.getTime() &&
            (end > rangeStart.getTime() ||
                (end === o.start.getTime() && o.start.getTime() >= rangeStart.getTime()))
        );
    };

    const firstYear = eventYear(event, timeZone);
    if (!event.repeatYearly) {
        const only: EventOccurrence = {
            start: event.start,
            end: event.end,
            year: firstYear,
            occurrenceKey: occurrenceKey(event.id, firstYear),
        };
        return overlaps(only) ? [only] : [];
    }

    // One year of slack each side covers zones and multi-day spans.
    const fromYear = Math.max(firstYear, rangeStart.getUTCFullYear() - 1);
    const toYear = rangeEnd.getUTCFullYear() + 1;
    const out: EventOccurrence[] = [];
    for (let year = fromYear; year <= toYear; year++) {
        const o = occurrenceInYear(event, year, timeZone);
        if (overlaps(o)) out.push(o);
    }
    return out;
}

/** The local calendar date an occurrence falls on: its UTC date when all-day. */
export function occurrenceDateKey(
    occurrence: { start: Date },
    isAllDay: boolean,
    timeZone: string,
): string {
    return isAllDay
        ? allDayDateKey(occurrence.start)
        : dayjs(occurrence.start).tz(timeZone).format("YYYY-MM-DD");
}

/** `YYYY-MM-DD` plus `days` (calendar arithmetic, no zone). */
export function addDaysToDateKey(dateKey: string, days: number): string {
    const [y, m, d] = dateKey.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/**
 * The occurrence a lead-time reminder sent on local `todayKey` is about: the
 * one whose date is `leadTimeDays` later. Null when there is none.
 */
export function leadTimeOccurrence(
    event: RepeatableEvent & { leadTimeDays: number | null },
    todayKey: string,
    timeZone: string,
): EventOccurrence | null {
    if (event.leadTimeDays === null || event.leadTimeDays < 1) return null;
    const targetKey = addDaysToDateKey(todayKey, event.leadTimeDays);
    // A two-day UTC window around the target date covers every zone.
    const from = new Date(`${addDaysToDateKey(targetKey, -1)}T00:00:00Z`);
    const to = new Date(`${addDaysToDateKey(targetKey, 2)}T00:00:00Z`);
    return (
        expandYearly(event, from, to, timeZone).find(
            (o) => occurrenceDateKey(o, event.isAllDay, timeZone) === targetKey,
        ) ?? null
    );
}

/** "tomorrow", "in 1 week", "in 2 weeks", "in 30 days". */
export function leadTimeWhen(leadTimeDays: number): string {
    if (leadTimeDays === 1) return "tomorrow";
    if (leadTimeDays % 7 === 0 && leadTimeDays <= 28) {
        const weeks = leadTimeDays / 7;
        return weeks === 1 ? "in 1 week" : `in ${weeks} weeks`;
    }
    return `in ${leadTimeDays} days`;
}

/**
 * One "Coming up" line in the morning summary push:
 * "Renewal – Acme insurance in 2 weeks" (no type prefix for plain events).
 */
export function leadTimeLine(kind: CompassEventKind, title: string, leadTimeDays: number): string {
    const prefix = kind === "EVENT" ? "" : `${eventKindLabel(kind)} – `;
    return `${prefix}${title} ${leadTimeWhen(leadTimeDays)}`;
}

// ---- updates -------------------------------------------------------------------

const isUtcMidnight = (d: Date) =>
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0;

const sameInstant = (a: Date | null | undefined, b: Date | null | undefined) =>
    (a?.getTime() ?? null) === (b?.getTime() ?? null);

/**
 * What an update does to `isAllDay`, judged on the MERGED row (stored values
 * overlaid with the patch), not on the patch alone.
 *
 * - `calendarReminderTypes` off: the client sends a timed form and never
 *   `isAllDay`. An all-day row whose start or end the user actually changed
 *   becomes a timed event (the times the form showed are what they meant);
 *   an all-day row saved with its dates untouched stays all-day.
 * - Flag on: the merged row must be a valid all-day row when it is one
 *   (UTC-midnight start/end), whether or not the patch carried `isAllDay`.
 */
export function resolveAllDayUpdate(
    stored: { start: Date; end: Date | null; isAllDay: boolean },
    patch: { start?: Date; end?: Date | null; isAllDay?: boolean },
    reminderTypesEnabled: boolean,
): { ok: true; isAllDay: boolean } | { ok: false; message: string } {
    const start = patch.start ?? stored.start;
    const end = patch.end === undefined ? stored.end : patch.end;
    if (end && end < start) return { ok: false, message: "End date cannot be before start date" };

    if (!reminderTypesEnabled) {
        const moved =
            (patch.start !== undefined && !sameInstant(patch.start, stored.start)) ||
            (patch.end !== undefined && !sameInstant(patch.end, stored.end));
        return { ok: true, isAllDay: stored.isAllDay && !moved };
    }

    const isAllDay = patch.isAllDay ?? stored.isAllDay;
    if (isAllDay && !(isUtcMidnight(start) && (!end || isUtcMidnight(end)))) {
        return {
            ok: false,
            message: "An all-day event starts and ends at midnight UTC of its dates",
        };
    }
    return { ok: true, isAllDay };
}

// ---- all-day form values -------------------------------------------------------

/** The stored start of an all-day entry whose first day is `dateKey`. */
export function allDayStartFromKey(dateKey: string): Date {
    return new Date(`${dateKey}T00:00:00Z`);
}

/** The stored (exclusive) end of an all-day entry whose last day is `lastDateKey`. */
export function allDayEndFromLastKey(lastDateKey: string): Date {
    return new Date(`${addDaysToDateKey(lastDateKey, 1)}T00:00:00Z`);
}
